<template>
  <div class="flex flex-col w-full h-full">
    <!-- Page header -->
    <div class="flex items-center gap-3 px-4 pt-4">
      <span
        class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500"
      >
        <span class="material-icons-round">domain</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">
          Corporate Management
        </h2>
        <p class="text-sm text-gray-500">
          Manage corporate accounts and their settings.
        </p>
      </div>
    </div>
    <div class="flex-1 min-h-0 m-4 mt-3 overflow-hidden">
      <div
        class="flex w-full h-full overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl"
      >
        <!-- Sidebar: corporate list -->
        <aside
          class="flex flex-col w-1/4 h-full border-r border-gray-200 bg-gray-50"
        >
          <div class="p-4 space-y-3 border-b border-gray-200">
            <router-link
              to="/corporate/add"
              class="inline-flex items-center justify-center w-full h-10 text-sm font-semibold text-white transition-colors duration-150 rounded-xl shadow-sm bg-taxiye-500 hover:bg-taxiye-600"
            >
              <span class="mr-1 text-base material-icons-round">add</span>
              Add Corporate
            </router-link>

            <div class="relative">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <span class="text-base text-gray-500 material-icons-round"
                  >search</span
                >
              </span>
              <input
                type="text"
                v-model="searchKey"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search corporates..."
              />
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <button
              type="button"
              v-for="(corporate, index) in corporates"
              :key="corporate._id"
              @click="selectCorporate(index)"
              class="flex items-center w-full px-4 py-3 space-x-3 text-left transition-colors duration-150 border-b border-gray-100 hover:bg-taxiye-50 focus:outline-none"
              :class="
                selectedCorporate && corporate._id === selectedCorporate._id
                  ? 'bg-taxiye-50 border-l-4 border-l-taxiye-500'
                  : 'border-l-4 border-l-transparent'
              "
            >
              <span
                class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold text-teal-700 rounded-full bg-teal-100"
                >{{ initials(corporate.name) }}</span
              >
              <div class="min-w-0">
                <h1 class="text-sm font-semibold text-gray-900 truncate">
                  {{ corporate.name }}
                </h1>
                <p class="text-xs text-gray-500 truncate">
                  {{ corporate.email }}
                </p>
              </div>
            </button>

            <p
              v-if="!corporates.length"
              class="px-4 py-6 text-sm text-center text-gray-400"
            >
              No corporates found.
            </p>
          </div>
        </aside>

        <!-- Detail pane -->
        <section class="flex flex-col w-3/4 h-full overflow-y-auto">
          <div v-if="selectedCorporate !== null" class="flex flex-col h-full">
            <!-- Header -->
            <div
              class="flex items-center justify-between px-5 py-4 border-b border-gray-200"
            >
              <div class="flex items-center space-x-3">
                <span
                  class="flex items-center justify-center flex-shrink-0 w-11 h-11 text-sm font-bold text-teal-700 rounded-full bg-teal-100"
                  >{{ initials(selectedCorporate.name) }}</span
                >
                <div>
                  <h2 class="text-lg font-semibold text-gray-900">
                    {{ selectedCorporate.name }}
                  </h2>
                  <p class="text-xs text-gray-500">
                    {{ selectedCorporate.email }}
                  </p>
                </div>
              </div>

              <label class="inline-flex items-center space-x-2 cursor-pointer">
                <span class="text-xs font-medium text-gray-600">
                  {{ selectedCorporate.active ? "Active" : "Inactive" }}
                </span>
                <span class="relative switch">
                  <input
                    type="checkbox"
                    @change="updateState"
                    v-model="selectedCorporate.active"
                  />
                  <span class="slider round"></span>
                </span>
              </label>
            </div>

            <!-- Tabs -->
            <nav class="flex px-3 space-x-1 border-b border-gray-200">
              <button
                type="button"
                v-for="tab in tabs"
                :key="tab.key"
                @click="selectTab(tab.key)"
                class="relative px-4 py-3 text-sm font-semibold transition-colors duration-150 focus:outline-none"
                :class="
                  selectedTab === tab.key
                    ? 'text-taxiye-600 border-b-2 border-taxiye-500'
                    : 'text-gray-500 border-b-2 border-transparent hover:text-gray-800'
                "
              >
                {{ tab.label }}
              </button>
            </nav>

            <!-- Tab content -->
            <div class="flex-1 p-5 overflow-y-auto">
              <Profile
                v-if="selectedTab == 'profile'"
                :corporate="selectedCorporate"
              ></Profile>
              <Rides
                v-if="selectedTab == 'rides'"
                :corporate="selectedCorporate"
              ></Rides>
              <Tickets
                v-if="selectedTab == 'tickets'"
                :corporate="selectedCorporate"
              ></Tickets>
              <CorporateRbac
                v-if="selectedTab == 'rbac'"
                :corporate="selectedCorporate"
              ></CorporateRbac>
              <Accounts
                v-if="selectedTab == 'accounts'"
                :corporate="selectedCorporate"
              ></Accounts>
              <CorporatePricing
                v-if="selectedTab == 'pricing'"
                :corporate="selectedCorporate"
              ></CorporatePricing>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else
            class="flex flex-col items-center justify-center h-full text-center"
          >
            <span class="text-5xl text-gray-300 material-icons-round"
              >business</span
            >
            <h3 class="mt-3 text-base font-semibold text-gray-700">
              No corporate selected
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              Select a corporate from the list on the left to view details.
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import Rides from "@/views/Corporate/Rides";
import Profile from "@/views/Corporate/Profile";
import Tickets from "@/views/Corporate/Tickets";
import CorporateRbac from "@/views/Corporate/CorporateRbac";
import Accounts from "@/views/Corporate/Accounts/Accounts";
import CorporatePricing from "@/views/Corporate/CorporatePricing";
export default {
  components: {
    Rides,
    Profile,
    Tickets,
    Accounts,
    CorporateRbac,
    CorporatePricing,
  },
  data() {
    return {
      corporates: [],
      selectedCorporate: null,
      selectedTab: "profile",
      loading: false,
      searchKey: "",
      tabs: [
        { key: "profile", label: "Profile" },
        { key: "rides", label: "Trips" },
        { key: "tickets", label: "Tickets" },
        { key: "rbac", label: "RBAC" },
        { key: "accounts", label: "Users" },
        { key: "pricing", label: "Pricings" },
      ],
    };
  },
  watch: {
    selectedCorporate() {},
    searchKey() {
      this.getCorporates();
    },
    $route: {
      handler() {
        this.selectedTab = "profile";
      },
      deep: true,
    },
  },
  created() {
    this.getCorporates();
    this.getCorporate(this.$route.params.id).then(({ data }) => {
      // console.log(JSON.stringify(corporate, null, 2))
      this.selectedCorporate = data;
    });
  },
  methods: {
    initials(name) {
      if (!name) return "?";
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join("");
    },
    getCorporates() {
      return this.$http
        .get("/corporates?q=" + this.searchKey)
        .then(response => (this.corporates = response.data.data))
        .catch(err => console.log(err));
    },
    getCorporate(id) {
      return this.$http.get("/corporates/" + id);
    },
    selectCorporate(index) {
      this.selectedCorporate = this.corporates[index];
      this.$router.push(`/corporate/${this.selectedCorporate._id}`);
    },
    updateState() {
      this.$http
        .patch("/corporates/" + this.selectedCorporate._id, {
          active: this.selectedCorporate.active,
        })
        .then(response => console.log(response))
        .catch(err => console.log(err));
    },
    selectTab(tab) {
      this.selectedTab = tab;
    },
  },
};
</script>

<style scoped>
/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d1d5db;
  transition: 0.3s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: 0.3s;
}

input:checked + .slider {
  background-color: #ee4b2b;
}

input:focus + .slider {
  box-shadow: 0 0 0 2px rgba(238, 75, 43, 0.3);
}

input:checked + .slider:before {
  transform: translateX(20px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>
